import { useEffect, useRef, useState } from 'react';
import Page from '../../../components/Page';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { decryptString, getAttachmentAssetUrl, getAttachmentPreviewSrc, isImageMediaType } from '../../../utils/url';
import { useAppDispatch, useAppSelector } from '../../../store';
import { Advertise, Attachments } from '../../../store/advertise/api/interface';
import { getAdvertise, patchAdvertise } from '../../../store/advertise/api';
import { createChangeAdvertiseStatus } from '../../../store/changeAdvertiseStatus/api';
import Loader from '../../../components/Loader';
import { MdOutlineTitle } from 'react-icons/md';
import { MdDescription } from 'react-icons/md';
import { MdNumbers } from 'react-icons/md';
import { GrStatusGoodSmall } from 'react-icons/gr';
import { selectAdvertiseStatusColor } from '../../../utils/selectAdvertiseStatusColor';
import Label from '../../../components/Label';
import { BiSolidCategory } from 'react-icons/bi';
import { TbCategory } from 'react-icons/tb';
import { DateFormat } from '../../../utils/formatTime';
import { IoCalendarNumber } from 'react-icons/io5';
import { IoIosPricetag } from 'react-icons/io';
import { FaLocationDot } from 'react-icons/fa6';
import { FaAddressBook } from 'react-icons/fa';
import { IoPersonSharp } from 'react-icons/io5';
import { isValidArray } from '../../../utils/valid';
import CloseIcon from '@mui/icons-material/Close';
import Scrollbar from '../../../components/Scrollbar';
import Iconify from '../../../components/Iconify';
import useToggle from '../../../hooks/useToggle';
import ChangeAdvertiseStatusFormModal from './ChangeStatusModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AdvertiseStatus, DocumentType, PropertyPossessionType } from '../../../enum';
import { publishAdsWithCredits } from '../../../services/company-credits';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { createChangeAdvertiseMediaPosition } from '../../../store/changeAdvertiseMediaPosition/api';
import { toast } from 'react-toastify';
import { setNavigatingWithinAdvertise } from '../shared-state';
import UploadAssetsModal from '../../../components/UploadAssetsModal';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { createIndividualAdDocument } from '../../../services/individual-ad-documents';

type Params = {
  id: string;
};

const index = () => {
  const dispatch = useAppDispatch();
  const params = useParams<Params>();
  const navigation = useNavigate();
  const location = useLocation();
  const decodeParam = decryptString(params.id ?? '');
  const { isLoading } = useAppSelector((state) => state.advertise);
  const [advertise, setAdvertise] = useState<Advertise>();
  const { toggle, onOpen, onClose } = useToggle();
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [suspendReason, setSuspendReason] = useState<string>('');
  const [openSuspendDialog, setOpenSuspendDialog] = useState(false);
  const [openPublishDialog, setOpenPublishDialog] = useState(false);
  const [openResubmitDialog, setOpenResubmitDialog] = useState(false);
  
  // Document upload modals
  const { toggle: titleDeedModalToggle, onOpen: onOpenTitleDeedModal, onClose: onCloseTitleDeedModal } = useToggle();
  const { toggle: individualIDModalToggle, onOpen: onOpenIndividualIDModal, onClose: onCloseIndividualIDModal } = useToggle();

  useEffect(() => {
    const fetchById = async () => {
      try {
        const record = await dispatch(
          getAdvertise({ id: decodeParam })
        ).unwrap();
        setAdvertise(record);
      } catch (error: any) {
        // Navigate based on user role
        if (userRole === 'company') {
          navigation('/company-advertise');
        } else {
          navigation('/advertise');
        }
      }
    };
    fetchById();
  }, []);

  const handleBack = async () => {
    // Set the flag to true when going back to the listing
    setNavigatingWithinAdvertise(true);
    
    // Use browser history navigation to go back
    if (location.state && location.state.fromListing) {
      navigation(-1); // Navigate back in history
    } else {
      // If we accessed the page directly, go to the listing based on user role
      if (userRole === 'company') {
        navigation('/company-advertise');
      } else {
        navigation('/advertise');
      }
    }
  };

  const handleLinkClick = (e: any) => {
    e.preventDefault();
    const mapUrl = `https://www.google.com/maps?q=${advertise?.latitude},${advertise?.longitude}`;
    window.open(mapUrl, '_blank', 'noopener,noreferrer');
  };

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [activeLightBoxIndex, setActiveLightBoxIndex] = useState<number>(0);
  const [attachments, setAttachments] = useState<Attachments[]>([]);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (advertise) {
      if (advertise?.attachments?.length > 0) {
        setAttachments(advertise?.attachments ?? []);
      }
    }
  }, [advertise?.attachments]);

  const handleOpenLightBox = (idx: number) => {
    setOpenLightBox(true);
    setActiveLightBoxIndex(idx);
  };

  const handleCloseLightBox = () => {
    setRotation(0);
    setOpenLightBox(false);
    setActiveLightBoxIndex(0);
  };

  const handleNextImage = () => {
    setRotation(0);
    if (activeLightBoxIndex === attachments?.length - 1) return;
    setActiveLightBoxIndex((curr) => ++curr);
  };

  const handlePrvImage = () => {
    setRotation(0);
    if (activeLightBoxIndex === 0) return;
    setActiveLightBoxIndex((curr) => --curr);
  };

  const handleRotate = () => {
    setRotation(rotation + 90);
  };

  const handleSuspendStatus = async () => {
    setOpenSuspendDialog(true);
  };

  const handleConfirmSuspend = async () => {
    try {
      await dispatch(
        createChangeAdvertiseStatus({
          advertiseId: advertise?.id ?? '',
          status: AdvertiseStatus.suspended,
          rejectionReason: suspendReason,
        })
      ).unwrap();
      const record = await dispatch(getAdvertise({ id: decodeParam })).unwrap();
      setAdvertise(record);
      setSuspendReason('');
      setOpenSuspendDialog(false);
    } catch (error: any) {}
  };

  const handleCloseSuspendDialog = () => {
    setOpenSuspendDialog(false);
    setSuspendReason('');
  };

  const handleResubmitClick = () => {
    setOpenResubmitDialog(true);
  };

  const handleCloseResubmitDialog = () => {
    setOpenResubmitDialog(false);
  };

  const handleConfirmResubmit = async () => {
    if (!advertise?.id) return;
    
    try {
      // Get attachment IDs from both attachmentArray and attachments
      const attachmentIds: string[] = [];
      if (advertise.attachmentArray && advertise.attachmentArray.length > 0) {
        attachmentIds.push(...advertise.attachmentArray);
      }
      if (advertise.attachments && advertise.attachments.length > 0) {
        const attachmentIdsFromAttachments = advertise.attachments
          .map((att: Attachments) => att.id)
          .filter((id: string) => id && !attachmentIds.includes(id));
        attachmentIds.push(...attachmentIdsFromAttachments);
      }
      // Remove duplicates
      const uniqueAttachmentIds = Array.from(new Set(attachmentIds));

      // Prepare propertyFeatureItems (must be array, not empty)
      const propertyFeatureItems = advertise.advertise_property_feature_mappers?.map((mapper: any) => ({
        featureId: mapper.featureId,
        value: parseFloat(mapper.value) || 0,
      })) || [];

      // Prepare propertyFacilityIds (must be array)
      const propertyFacilityIds = advertise.advertise_property_facility_mappers?.map((mapper: any) => mapper.facilityId).filter((id: string) => id) || [];

      // Prepare all required fields for PATCH (backend requires all fields when status is not draft)
      // Preserve original createdBy to maintain ownership (important for agent ads)
      const patchData: any = {
        id: advertise.id,
        title: advertise.title || '',
        description: advertise.description || '',
        zoneNumber: advertise.zoneNumber || '',
        streetNumber: advertise.streetNumber || '',
        buildingNumber: advertise.buildingNumber || '',
        zipCode: advertise.zipCode || '',
        address: advertise.address || '',
        latitude: advertise.latitude || 0,
        longitude: advertise.longitude || 0,
        possessionTypeId: advertise.possessionTypeId || '',
        price: parseFloat(advertise.price) || 0,
        status: AdvertiseStatus.submit,
        locationId: advertise.location_management?.id || advertise.categoryId || '',
        categoryId: advertise.categoryId || '',
        subCategoryId: advertise.subCategoryId || '',
        attachmentArray: uniqueAttachmentIds.length > 0 ? uniqueAttachmentIds : [],
        propertyFeatureItems: propertyFeatureItems,
        propertyFacilityIds: propertyFacilityIds,
      };

      // Add rentFrequency only if possessionTypeId is for rent
      if (advertise.rentFrequency) {
        patchData.rentFrequency = advertise.rentFrequency;
      }

      // Add areaPolygon if it exists (format as string, not array)
      if ((advertise as any).areaPolygon) {
        patchData.areaPolygon = (advertise as any).areaPolygon;
      }

      // Use PATCH /advertise/:id with all required fields (same as mobile app)
      await dispatch(patchAdvertise(patchData)).unwrap();
      
      toast.success('Ad resubmitted successfully. It will be reviewed by admin.');
      
      // Refresh the ad data
      const updatedRecord = await dispatch(
        getAdvertise({ id: decodeParam })
      ).unwrap();
      setAdvertise(updatedRecord);
      
      setOpenResubmitDialog(false);
    } catch (error: any) {
      console.error('Error resubmitting ad:', error);
      toast.error(error?.message || 'Failed to resubmit ad.');
    }
  };

  const preprocessAttachments = (attachments: Attachments[]) => {
    const images = attachments.filter((item) =>
      isImageMediaType(item.fileType, item.fileName)
    );
    const videos = attachments.filter(
      (item) => item.fileType?.split('/')[0] === 'video'
    );
    const others = attachments.filter(
      (item) =>
        !isImageMediaType(item.fileType, item.fileName) &&
        item.fileType?.split('/')[0] !== 'video'
    );
    return [...images, ...videos, ...others];
  };

  const [advertiseAttachments, setAdvertiseAttachments] = useState<
    Attachments[]
  >(preprocessAttachments(advertise?.attachments || []));

  useEffect(() => {
    // Ensure attachments stay sorted whenever `advertise?.attachments` changes
    if (advertise?.attachments) {
      setAdvertiseAttachments(preprocessAttachments(advertise.attachments));
    }
  }, [advertise?.attachments]);

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    // If dropped outside the list or no move was made
    if (!destination || destination.index === source.index) {
      return;
    }

    // Get the images and non-image attachments
    const images = attachments.filter((item) =>
      isImageMediaType(item.fileType, item.fileName)
    );
    const nonImages = attachments.filter(
      (item) => !isImageMediaType(item.fileType, item.fileName)
    );

    // Perform drag-and-drop reordering only on images
    const [movedImage] = images.splice(source.index, 1);
    images.splice(destination.index, 0, movedImage);

    // Combine reordered images and non-image attachments
    const newAttachments = [...images, ...nonImages];
    setAdvertiseAttachments(newAttachments);
    setAttachments(newAttachments);

    // Send updated order of image IDs to the backend
    sendToBackend(newAttachments);
  };

    const sendToBackend = async (updatedAttachments: Attachments[]) => {
    const imageIds = updatedAttachments
      .filter((item) => isImageMediaType(item.fileType, item.fileName))
      .map((item) => item.id);

    const mediaPositionResult = await dispatch(
      createChangeAdvertiseMediaPosition({
        advertiseId: decodeParam,
        attachmentArray: imageIds,
      })
    ).unwrap();

    if (mediaPositionResult?.message) {
      toast.success(mediaPositionResult.message);
    }
  };

  // Handle Title Deed upload
  const handleTitleDeedUpload = async (assetIds: string[]) => {
    if (assetIds.length > 0) {
      try {
        await createIndividualAdDocument({
          advertiseId: decodeParam,
          documentType: 'title_deed',
          assetId: assetIds[0],
        });
        toast.success('Title Deed uploaded successfully');
        // Refresh advertise data
        const record = await dispatch(getAdvertise({ id: decodeParam })).unwrap();
        setAdvertise(record);
        onCloseTitleDeedModal();
      } catch (error: any) {
        toast.error(error.message || 'Failed to upload Title Deed');
      }
    }
  };

  // Handle Individual ID upload
  const handleIndividualIDUpload = async (assetIds: string[]) => {
    if (assetIds.length > 0) {
      try {
        await createIndividualAdDocument({
          advertiseId: decodeParam,
          documentType: 'individual_id',
          assetId: assetIds[0],
        });
        toast.success('ID document uploaded successfully');
        // Refresh advertise data
        const record = await dispatch(getAdvertise({ id: decodeParam })).unwrap();
        setAdvertise(record);
        onCloseIndividualIDModal();
      } catch (error: any) {
        toast.error(error.message || 'Failed to upload ID document');
      }
    }
  };

  // Handle publish with credits for company admin
  const handlePublishClick = () => {
    setOpenPublishDialog(true);
  };

  const handleClosePublishDialog = () => {
    setOpenPublishDialog(false);
  };

  const handleConfirmPublish = async () => {
    if (!advertise?.id) return;
    
    try {
      const result = await publishAdsWithCredits({
        advertiseIds: [advertise.id],
      });
      
      toast.success(result.message || 'Ad published successfully using credits.');
      
      // Refresh the ad data
      const updatedRecord = await dispatch(
        getAdvertise({ id: decodeParam })
      ).unwrap();
      setAdvertise(updatedRecord);
      
      setOpenPublishDialog(false);
    } catch (error: any) {
      console.error('Error publishing ad with credits:', error);
      toast.error(error?.message || 'Failed to publish ad. Please check your credit balance.');
    }
  };

  // Check if this is an individual user's ad
  const isIndividualUserAd = (advertise?.createdByUser as any)?.userRole === 'individual';
  const isAdmin = userRole === 'admin' || userRole === 'adminTier2';

  return (
    <Page title={'View Advertise'}>
      <Container maxWidth={'xl'}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          spacing={{ xs: 0.5, sm: 1.5 }}
          my={1}
        >
          {/* Change Status button - only for admins to approve/reject submitted ads */}
          {advertise?.status === AdvertiseStatus.submit && isAdmin && (
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:edit-outline'} />}
              onClick={() => onOpen()}
            >
              {'Change Status'}
            </Button>
          )}

          {/* Publish button for company admins when status is approved */}
          {userRole === 'company' && advertise?.status === AdvertiseStatus.approved && (
            <Button
              variant='contained'
              color='success'
              startIcon={<Iconify icon={'eva:checkmark-circle-2-fill'} />}
              onClick={handlePublishClick}
            >
              {'Publish Ad'}
            </Button>
          )}

          {/* Republish button for company admins when status is expired */}
          {userRole === 'company' && advertise?.status === AdvertiseStatus.expired && (
            <Button
              variant='contained'
              color='success'
              startIcon={<Iconify icon={'eva:refresh-outline'} />}
              onClick={handlePublishClick}
            >
              {'Republish Ad'}
            </Button>
          )}

          {/* Resubmit button for company admins when status is suspended or rejected */}
          {userRole === 'company' && (advertise?.status === AdvertiseStatus.suspended || advertise?.status === AdvertiseStatus.rejected) && (
            <Button
              variant='contained'
              color='primary'
              startIcon={<Iconify icon={'eva:checkmark-circle-2-fill'} />}
              onClick={handleResubmitClick}
            >
              {'Resubmit'}
            </Button>
          )}

          {/* Suspend and Extend buttons - only for admins, not company admins */}
          {advertise?.status === AdvertiseStatus.published && isAdmin && (
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:edit-outline'} />}
              onClick={() => handleSuspendStatus()}
            >
              {'Suspend Advertise'}
            </Button>
          )}

          {(advertise?.status === AdvertiseStatus.published ||
            advertise?.status === AdvertiseStatus.expired) && isAdmin && (
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:edit-outline'} />}
              onClick={() => {
                // Store action type in a way the modal can access it
                // We'll pass it as a prop when rendering the modal
                onOpen();
              }}
            >
              {'Extend Expiry'}
            </Button>
          )}

          <Button
            onClick={handleBack}
            variant='outlined'
            startIcon={<ArrowBackIcon />}
          >
            {'Back'}
          </Button>
        </Stack>

        <Card>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <CardHeader title={'Advertisement Information'} />
              <Grid
                container
                spacing={2}
                mb={3}
                ml={1}
                mr={1}
                mt={1}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <MdOutlineTitle className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Title:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{advertise?.title}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <MdNumbers className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Number:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{advertise?.advertiseNumber}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IoPersonSharp className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>User Name:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{advertise?.createdByUser?.name}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <GrStatusGoodSmall className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Status:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      <Label
                        variant='ghost'
                        color={selectAdvertiseStatusColor(advertise?.status)}
                      >
                        {advertise?.status}
                      </Label>
                    </Typography>
                  </Stack>
                </Grid>

                {advertise?.status === AdvertiseStatus.suspended && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <Stack direction='row'>
                      <MdDescription className='user-profile-icon-class' />
                      <Typography variant='subtitle2'>Suspended Reason:</Typography>
                      <Typography variant='body2'>
                        &nbsp;{advertise?.rejectionReason}
                      </Typography>
                    </Stack>
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IoCalendarNumber className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Created Date Time:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{advertise?.createdAt && new Date(advertise?.createdAt).toLocaleString()}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <BiSolidCategory className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Category:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      {advertise?.property_category?.labelEn}
                    </Typography>
                  </Stack>
                </Grid>
                {advertise?.status === AdvertiseStatus.rejected && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <Stack direction='row'>
                      <MdDescription className='user-profile-icon-class' />
                      <Typography variant='subtitle2'>Rejection Reason:</Typography>
                      <Typography variant='body2'>
                        &nbsp;{advertise?.rejectionReason}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <TbCategory className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Sub Category:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      {advertise?.property_sub_category?.labelEn}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IoIosPricetag className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Price:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      {advertise?.price}
                      {advertise?.property_possession_type?.id ===
                        PropertyPossessionType.forRent &&
                        `/${advertise?.rentFrequency}`}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <TbCategory className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>
                      Possession Type:
                    </Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      {advertise?.property_possession_type?.labelEn}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IoCalendarNumber className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Start Date:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      {advertise?.startDate && DateFormat(advertise?.startDate)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IoCalendarNumber className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>End Date:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      {advertise?.endDate && DateFormat(advertise?.endDate)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <FaLocationDot className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Location:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      {advertise?.location_management &&
                        advertise?.location_management?.labelEn}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <FaLocationDot className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Co-ordinates:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      <a
                        href='#'
                        onClick={handleLinkClick}
                        style={{ color: '#a40466' }}
                        // color='#a40466'
                      >
                        Open Location in Maps
                      </a>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                >
                  <Stack
                    direction='row'
                    flexWrap='wrap'
                    // spacing={1}
                    alignItems='center'
                  >
                    <FaAddressBook className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Address:</Typography>
                    {advertise?.address && (
                      <Typography variant='body2'>
                        &nbsp;
                        {advertise?.address},
                      </Typography>
                    )}
                    {advertise?.buildingNumber && (
                      <Typography variant='body2'>
                        Building Number: {advertise?.buildingNumber ?? ''}
                        ,&nbsp;
                      </Typography>
                    )}
                    {advertise?.streetNumber && (
                      <Typography variant='body2'>
                        Street Number: {advertise?.streetNumber ?? ''},&nbsp;
                      </Typography>
                    )}
                    {advertise?.zoneNumber && (
                      <Typography variant='body2'>
                        Zone Number: {advertise?.zoneNumber ?? ''},&nbsp;
                      </Typography>
                    )}
                    {advertise?.zipCode && (
                      <Typography variant='body2'>
                        Zip Code: {advertise?.zipCode ?? ''}.
                      </Typography>
                    )}
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                >
                  <Stack direction='row'>
                    <MdDescription className='user-profile-icon-class' />
                    <Typography variant='subtitle2'>Description:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{advertise?.description}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}
        </Card>

        {isValidArray(advertise?.attachments) && (
          <Card sx={{ mt: 2 }}>
            <CardHeader title={'Images'} />
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId='droppable-attachments'
                direction='horizontal'
              >
                {(provided) => (
                  <Box
                    sx={{
                      whiteSpace: 'normal',
                      m: 2,
                      maxHeight: 150,
                      overflow: 'auto',
                      display: 'flex',
                    }}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {advertiseAttachments?.map((items, idx) => (
                      <Draggable
                        key={items.id}
                        draggableId={items.id.toString()}
                        index={idx}
                        isDragDisabled={
                          !isImageMediaType(items.fileType, items.fileName)
                        } // Disable drag for non-images
                      >
                        {(provided) => (
                          <div
                            style={{
                              width: '100px',
                              height: '100px',
                              maxWidth: '100%',
                              borderRadius: 'unset',
                              margin: '8px',
                              position: 'relative',
                              flexShrink: '0',
                            }}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                          >
                            <Tooltip title={items?.fileName}>
                              <img
                                id={items.key}
                                alt={items?.fileName}
                                src={getAttachmentPreviewSrc(items, accessToken)}
                                loading='lazy'
                                onClick={() => handleOpenLightBox(idx)}
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  maxWidth: '100%',
                                  borderRadius: 'unset',
                                  position: 'relative',
                                }}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
            {/* <Box
              sx={{
                whiteSpace: 'normal',
                m: 2,
                maxHeight: 150,
                overflow: 'auto',
                display: 'flex',
              }}
            >
              {advertise?.attachments?.map((items, imgIdx) => {
                if (items.fileType.split('/')[0] === 'image') {
                  return (
                    <div
                      style={{
                        width: '100px',
                        height: '100px',
                        maxWidth: '100%',
                        borderRadius: 'unset',
                        margin: '8px',
                        position: 'relative',
                        flexShrink: '0',
                      }}
                    >
                      <Tooltip
                        title={items?.fileName}
                        key={items?.id}
                      >
                        <img
                          id={items.key}
                          alt={items?.fileName}
                          src={getAssetURl(items?.key)}
                          crossOrigin={'anonymous'}
                          loading='lazy'
                          onClick={() => handleOpenLightBox(imgIdx)}
                          style={{
                            width: '100px',
                            height: '100px',
                            maxWidth: '100%',
                            borderRadius: 'unset',
                            position: 'relative',
                          }}
                        />
                      </Tooltip>
                    </div>
                  );
                }
              })}

              {advertise?.attachments?.map((items, imgIdx) => {
                if (
                  items?.fileType?.split('/')[0] === 'application' &&
                  items?.fileType?.split('/')[1] === 'pdf'
                ) {
                  return (
                    <div
                      style={{
                        width: '110px',
                        height: '110px',
                        maxWidth: '100%',
                        borderRadius: 'unset',
                        margin: '8px',
                        position: 'relative',
                        flexShrink: '0',
                      }}
                    >
                      <Tooltip
                        title={items?.fileName}
                        key={items?.id}
                      >
                        <img
                          src='/static/pdf.png'
                          style={{
                            width: '110px',
                            height: '110px',
                            maxWidth: '100%',
                            borderRadius: 'unset',
                            position: 'relative',
                          }}
                        />
                      </Tooltip>
                    </div>
                  );
                } else if (items.fileType.split('/')[0] === 'video') {
                  return (
                    <div
                      style={{
                        width: '110px',
                        height: '110px',
                        maxWidth: '100%',
                        borderRadius: 'unset',
                        margin: '8px',
                        position: 'relative',
                        flexShrink: '0',
                      }}
                    >
                      <Tooltip
                        title={items?.fileName}
                        key={items?.id}
                      >
                        <img
                          src='/static/video_placeholder.png'
                          onClick={() => handleOpenLightBox(imgIdx)}
                          style={{
                            width: '110px',
                            height: '110px',
                            maxWidth: '100%',
                            borderRadius: 'unset',
                            position: 'relative',
                          }}
                        />
                      </Tooltip>
                    </div>
                  );
                }
              })}
            </Box> */}
          </Card>
        )}

        {/* Documents Section for Individual User Ads - Admin Only */}
        {isIndividualUserAd && isAdmin && (
          <Card sx={{ mt: 2 }}>
            <CardHeader title={'Required Documents'} />
            <Box sx={{ p: 2 }}>
              <Grid container spacing={3}>
                {/* Title Deed Section */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Title Deed
                    </Typography>
                    {advertise?.titleDeed ? (
                      <Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: '200px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            mb: 1,
                          }}
                        >
                          <img
                            src={getAttachmentPreviewSrc(advertise.titleDeed, accessToken)}
                            alt={advertise.titleDeed.fileName}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {advertise.titleDeed.fileName}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={onOpenTitleDeedModal}
                          sx={{ mt: 1 }}
                        >
                          Replace Title Deed
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={<CloudUploadIcon />}
                          onClick={onOpenTitleDeedModal}
                        >
                          Upload Title Deed
                        </Button>
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                          Required for approval
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {/* Individual ID Section */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      ID Document
                    </Typography>
                    {advertise?.individualID ? (
                      <Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: '200px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            mb: 1,
                          }}
                        >
                          <img
                            src={getAttachmentPreviewSrc(advertise.individualID, accessToken)}
                            alt={advertise.individualID.fileName}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {advertise.individualID.fileName}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={onOpenIndividualIDModal}
                          sx={{ mt: 1 }}
                        >
                          Replace ID Document
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={<CloudUploadIcon />}
                          onClick={onOpenIndividualIDModal}
                        >
                          Upload ID Document
                        </Button>
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                          Required for approval
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Card>
        )}

        {isValidArray(advertise?.advertise_property_facility_mappers) && (
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title={'Property Facility'}
              sx={{ mb: 1 }}
            />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={8}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : advertise?.advertise_property_facility_mappers
                        ?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      advertise?.advertise_property_facility_mappers?.map(
                        (propertyFacility) => (
                          <TableRow key={propertyFacility.id}>
                            <TableCell align='center'>
                              {propertyFacility?.property_facility?.labelEn}
                            </TableCell>
                            <TableCell align='center'>
                              {propertyFacility?.property_facility?.labelAr}
                            </TableCell>
                            <TableCell align='center'>
                              <Label
                                variant='ghost'
                                color={
                                  propertyFacility?.isAvailable
                                    ? 'success'
                                    : 'error'
                                }
                              >
                                {propertyFacility?.isAvailable
                                  ? 'Active'
                                  : 'InActive'}
                              </Label>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        )}

        {isValidArray(advertise?.advertise_property_feature_mappers) && (
          <Card sx={{ mt: 2 }}>
            <CardHeader
              title={'Property Features'}
              sx={{ mb: 1 }}
            />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Value'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={8}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : advertise?.advertise_property_feature_mappers
                        ?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      advertise?.advertise_property_feature_mappers?.map(
                        (propertyFeature) => (
                          <TableRow key={propertyFeature.id}>
                            <TableCell align='center'>
                              {propertyFeature?.property_feature?.labelEn}
                            </TableCell>
                            <TableCell align='center'>
                              {propertyFeature?.property_feature?.labelAr}
                            </TableCell>
                            <TableCell align='center'>
                              {propertyFeature?.value}
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        )}

        {toggle && (
          <ChangeAdvertiseStatusFormModal
            // touched={touched}
            // errors={errors}
            // handleChange={handleChange}
            // handleSubmit={handleSubmit}
            // isSubmitting={isSubmitting}
            onClose={onClose}
            // resetForm={resetForm}
            // setFieldValue={setFieldValue}
            toggle={toggle}
            advertise={advertise}
            actionType={
              (advertise?.status === AdvertiseStatus.published || 
               advertise?.status === AdvertiseStatus.expired)
                ? 'extendExpiry' 
                : 'changeStatus'
            }
            setAdvertise={setAdvertise}
            // values={values}
          />
        )}

        <Dialog
          open={openSuspendDialog}
          onClose={handleCloseSuspendDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Suspend Advertise</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to suspend this advertise?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Suspend Reason"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSuspendDialog}>Cancel</Button>
            <Button 
              onClick={handleConfirmSuspend}
              variant="contained"
              disabled={!suspendReason.trim()}
            >
              Suspend
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openPublishDialog}
          onClose={handleClosePublishDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {advertise?.status === AdvertiseStatus.expired ? 'Republish Advertise' : 'Publish Advertise'}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {advertise?.status === AdvertiseStatus.expired
                ? 'Are you sure you want to republish this expired advertise? This will use 1 credit from your account.'
                : 'Are you sure you want to publish this advertise? This will use 1 credit from your account.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePublishDialog}>No</Button>
            <Button 
              onClick={handleConfirmPublish}
              variant="contained"
              color="success"
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openResubmitDialog}
          onClose={handleCloseResubmitDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Resubmit Advertise</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to resubmit this advertise? It will be sent for admin review.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseResubmitDialog}>No</Button>
            <Button 
              onClick={handleConfirmResubmit}
              variant="contained"
              color="primary"
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {openLightBox && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='myModal'
          className='modal'
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className={`modal-content ${
              openLightBox ? 'display-block ' : 'display-none'
            }`}
          >
            {attachments?.[activeLightBoxIndex] && (
              <div className='vertical-center-div'>
                <div
                  ref={boxRef}
                  className={`mySlides ${
                    openLightBox ? 'display-block ' : 'display-none'
                  }`}
                >
                  {isImageMediaType(
                    attachments?.[activeLightBoxIndex]?.fileType,
                    attachments?.[activeLightBoxIndex]?.fileName
                  ) ? (
                    <img
                      id={attachments?.[activeLightBoxIndex]?.key}
                      src={getAttachmentAssetUrl(
                        attachments?.[activeLightBoxIndex],
                        accessToken
                      )}
                      alt={attachments?.[activeLightBoxIndex]?.fileName}
                      style={{
                        transform: `rotate(${rotation}deg)`,
                        maxWidth: 'min(100%, 720px)',
                        maxHeight: 'min(80vh, 520px)',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <video
                      id={attachments?.[activeLightBoxIndex]?.key}
                      src={getAttachmentAssetUrl(
                        attachments?.[activeLightBoxIndex],
                        accessToken
                      )}
                      style={{
                        transform: `rotate(${rotation}deg)`,
                        maxWidth: 'min(100%, 720px)',
                        maxHeight: 'min(80vh, 520px)',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                      controls
                      autoPlay
                    />
                  )}
                </div>
              </div>
            )}
            <div
              className='prev'
              onClick={handlePrvImage}
            >
              <i className='fa-solid fa-arrow-left'></i>
            </div>
            <div
              className='next'
              onClick={handleNextImage}
            >
              <i className='fa-solid fa-arrow-right'></i>
            </div>
            <div
              className='rotate'
              onClick={handleRotate}
            >
              <i className='fa-solid fa-camera-rotate'></i>
            </div>
          </div>
        </Box>
      )}

      {/* Title Deed Upload Modal */}
      <UploadAssetsModal
        toggle={titleDeedModalToggle}
        onClose={onCloseTitleDeedModal}
        setAttachmentArray={handleTitleDeedUpload}
        module={DocumentType.TitleDeed}
        isSingleUpload={true}
        moduleId={decodeParam}
      />

      {/* Individual ID Upload Modal */}
      <UploadAssetsModal
        toggle={individualIDModalToggle}
        onClose={onCloseIndividualIDModal}
        setAttachmentArray={handleIndividualIDUpload}
        module={DocumentType.IndividualID}
        isSingleUpload={true}
        moduleId={decodeParam}
      />
    </Page>
  );
};

export default index;
